import gsap from "gsap";
import { $ } from "../utils/dom.js";

// q is used as a local shorthand for context.querySelector
const q = (selector, context = document) => context.querySelector(selector);


async function submitToWaitlist(email, ref) {
  try {
    const response = await fetch("https://api.getwaitlist.com/api/v1/signup", {
      method: "POST",
      headers: {
        "Authorization": "7effa1fa54c486e4b0448b6c72266436eacb963ea6280bb9132b231884126253",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        waitlist_id: 25431,
        email,
        referral_link: ref,
      }),
    });

    if (!response.ok) throw new Error(`HTTP error ${response.status}`);
    return await response.json();
  } catch (e) {
    console.error(e);
    return null;
  }
}

function validateEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).toLowerCase());
}

function getRefFromQuery() {
  try {
    if (!window.location.search) return null;
    const params = new URLSearchParams(window.location.search);
    const id = params.get("ref_id");
    return id ? `https://geniegetsme.com/waitlist?ref_id=${id}` : null;
  } catch {
    return null;
  }
}

function initCopy(refInput, refBtn) {
  if (!refInput || !refBtn) return;
  refBtn.addEventListener("click", () => {
    const v = refInput.value || "";
    if (!v) return;
    navigator.clipboard?.writeText(v).catch(() => { });
    refBtn.classList.add("copied");
    setTimeout(() => refBtn.classList.remove("copied"), 2000);
  });
}

function loadSuccessLottie(scope) {
  try {
    const container = q(".success__logo", scope);
    if (!container || !window.lottie) return;
    window.lottie.loadAnimation({
      container,
      renderer: "svg",
      loop: false,
      autoplay: true,
      path: "/lottie/logowhite.json",
      rendererSettings: { preserveAspectRatio: "xMidYMid slice" },
    });
  } catch { }
}

function applySuccessUI(data, scope = document) {
  const form = q(".form", scope);
  const successEl = q(".success", scope);
  const successInput = q(".success__copy input", scope);
  const successPosition = q(".success__position", scope);
  const xLink = q(".success__x", scope);
  const emailLink = q(".success__email", scope);
  const whatsappLink = q(".success__whatsapp", scope);
  const shareBtn = q(".success__share", scope);
  const closeBtn = q(".success__close", scope);


  const referral_link = data?.referral_link || "";
  const position = Math.max(0, (data?.priority || 1) - 1);

  if (successInput) successInput.value = referral_link;
  if (successPosition) successPosition.textContent = String(position);

  if (xLink) {
    if (window.innerWidth > 991) {
      xLink.href = `https://twitter.com/intent/tweet?text=${encodeURIComponent("Just signed up to Genie, you should too!")}&url=${encodeURIComponent(referral_link)}`;
    } else {
      xLink.href = `twitter://post?message=${encodeURIComponent("Just signed up to Genie, you should too! " + referral_link)}`;
    }
  }

  if (emailLink) {
    const body = `Just signed up to Genie, you should too! ${referral_link}`;
    emailLink.href = `mailto:?subject=Genie%20Waitlist&body=${encodeURIComponent(body)}`;
  }

  if (whatsappLink) {
    whatsappLink.href = `https://wa.me/?text=${encodeURIComponent("Just signed up to Genie, you should too! " + referral_link)}`;
  }

  if (shareBtn) {
    shareBtn.addEventListener("click", () => {
      try {
        if (navigator.share) {
          navigator.share({
            title: document.title,
            text: "Just signed up to Genie, you should too!",
            url: referral_link,
          }).catch(() => { });
        }
      } catch { }
    });
  }

  if (form) gsap.to(form, { autoAlpha: 0, duration: 0.4 });
  if (successEl) successEl.classList.add("show");

  loadSuccessLottie(scope);

  if (closeBtn) {
    closeBtn.addEventListener("click", (e) => {
      e.preventDefault();
      successEl?.classList.remove("show");
    }, { once: true });
  }
}

export function bindWaitlist(root) {
  if (!root || root.dataset.waitlistInited === "1") return;
  root.dataset.waitlistInited = "1";

  const submitButton = q(".form__submit", root);
  const emailInput = q(".form__email", root);
  const successInput = q(".success__copy input", root);
  const successCopy = q(".success__copy button", root);

  initCopy(successInput, successCopy);

  if (!submitButton || !emailInput) return;

  async function handleSubmit(e) {
    e.preventDefault();
    const email = emailInput.value.trim();
    const ref = getRefFromQuery();

    if (!email || !validateEmail(email)) {
      emailInput.classList.add("error");
      return;
    }
    emailInput.classList.remove("error");

    const data = await submitToWaitlist(email, ref);
    if (data) {
      applySuccessUI(data, root);
    } else {
      emailInput.classList.add("error");
    }
  }

  submitButton.addEventListener("click", handleSubmit);
  emailInput.addEventListener("keypress", (e) => {
    if (e.key === "Enter") handleSubmit(e);
  });
}

function initInvestorsHover() {
  const btn = q(".footer__main__investors");
  if (!btn) return;

  const text = q(".footer__main__investors__text", btn);
  const email = q(".footer__main__investors__email", btn);
  if (!text || !email) return;

  // Measure initial width (Investors)
  const initialWidth = btn.offsetWidth;

  // Measure email width accurately by temporarily removing absolute positioning
  const originalPos = email.style.position;
  const originalOpacity = email.style.opacity;
  const originalVisibility = email.style.visibility;

  email.style.position = 'relative';
  email.style.opacity = '0';
  email.style.visibility = 'hidden';
  const emailWidthVal = btn.offsetWidth;

  // Restore
  email.style.position = originalPos;
  email.style.opacity = originalOpacity;
  email.style.visibility = originalVisibility;

  // Set initial width and performance hints
  gsap.set(btn, { width: initialWidth, willChange: 'width' });
  gsap.set([text, email], { willChange: 'transform, opacity' });

  let tl = gsap.timeline({ paused: true, defaults: { duration: 0.4, ease: "custom" } });

  tl.to(btn, { width: emailWidthVal }, 0);
  tl.to(text, { yPercent: -100, opacity: 0 }, 0);
  tl.to(email, { yPercent: -150, opacity: 1 }, 0);

  btn.addEventListener("mouseenter", () => tl.play());
  btn.addEventListener("mouseleave", () => tl.reverse());
}

export default function initFooterWaitlist() {
  const footer = $(".footer");
  if (footer) {
    bindWaitlist(footer);
    initInvestorsHover();
  }



  fetch("https://api.getwaitlist.com/api/v1/waitlist?waitlist_id=25431", {
    method: "GET",
    headers: {
      "Authorization": "7effa1fa54c486e4b0448b6c72266436eacb963ea6280bb9132b231884126253",
      "Content-Type": "application/json",
    },
  })
    .then((r) => r.json())
    .then((d) => {
      // Available if needed later
      if (footer) footer.dataset.totalSignups = String(d?.statistics?.total_signups || 0);
    })
    .catch(() => { });
}
